/**
 * Two small SVG charts for History, no chart library.
 * EquityLines: one or two equity curves (strategy vs benchmark) on one y-axis, 2px lines, hover tooltip.
 * MonthlyBars: P&L per month as columns from a zero baseline (position + colour + signed tooltip).
 */
import React, { useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { t } from '../utils/theme';
import { inr } from './ui';

export interface Series { name: string; color: string; points: { day: string; value: number }[] }
export interface MonthPoint { month: string; pnl: number }

const H = 240;
const PAD = { top: 12, right: 12, bottom: 26, left: 64 };

function niceTicks(min: number, max: number, count = 4, includeZero = true): number[] {
  const lo = includeZero ? Math.min(0, min) : min, hi = includeZero ? Math.max(0, max) : max;
  const span = hi - lo || 1;
  const raw = span / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => span / s <= count + 1) || raw;
  const ticks = [];
  for (let v = Math.floor(lo / step) * step; v <= hi + step * 0.001; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

const compact = (v: number) => {
  const a = Math.abs(v);
  const s = a >= 1e7 ? `${(a / 1e7).toFixed(2)}Cr` : a >= 1e5 ? `${(a / 1e5).toFixed(1)}L` : a >= 1e3 ? `${(a / 1e3).toFixed(a >= 1e4 ? 0 : 1)}k` : `${a}`;
  return `${v < 0 ? '−' : ''}₹${s}`;
};
const fmtDay = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
const fmtMonth = (ym: string) => new Date(`${ym}-01`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(640);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, w };
}

const Tip: React.FC<{ x: number; w: number; children: React.ReactNode }> = ({ x, w, children }) => (
  <Box sx={{
    position: 'absolute', top: 4, pointerEvents: 'none', zIndex: 2, left: Math.min(Math.max(x + 12, 0), w - 210),
    bgcolor: '#1E293B', border: '1px solid #334155', borderRadius: 1.5, px: 1.5, py: 1, minWidth: 190,
  }}>{children}</Box>
);

const Row: React.FC<{ k: React.ReactNode; v: React.ReactNode }> = ({ k, v }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '0.8rem', color: t.text }}>
    <span style={{ color: t.text2 }}>{k}</span><b>{v}</b>
  </Box>
);

export const EquityLines: React.FC<{ series: Series[] }> = ({ series }) => {
  const { ref, w } = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const base = series[0]?.points ?? [];
  const byDay = useMemo(() => series.map((s) => new Map(s.points.map((p) => [p.day, p.value]))), [series]);
  const values = series.flatMap((s) => s.points.map((p) => p.value));
  const ticks = useMemo(() => niceTicks(Math.min(...values), Math.max(...values), 4, false), [values]);
  if (!base.length) return null;
  const yMin = ticks[0], yMax = ticks[ticks.length - 1];
  const innerW = w - PAD.left - PAD.right, innerH = H - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (base.length > 1 ? (innerW * i) / (base.length - 1) : innerW / 2);
  const y = (v: number) => PAD.top + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;
  const labelEvery = Math.ceil(base.length / Math.max(1, Math.floor(innerW / 90)));

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
        {series.map((s) => (
          <Stack key={s.name} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 14, height: 3, borderRadius: 2, bgcolor: s.color }} />
            <Typography variant="caption" sx={{ color: t.text2 }}>{s.name}</Typography>
          </Stack>
        ))}
      </Stack>
      <Box ref={ref} sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
        <svg width={w} height={H} role="img" aria-label="Portfolio value over time" style={{ display: 'block' }}>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={PAD.left} x2={w - PAD.right} y1={y(v)} y2={y(v)} stroke={t.border} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end" fill={t.text3} fontSize={11}>{compact(v)}</text>
            </g>
          ))}
          {base.map((p, i) => i % labelEvery === 0 && (
            <text key={p.day} x={x(i)} y={H - 8} textAnchor="middle" fill={t.text3} fontSize={11}>{fmtDay(p.day)}</text>
          ))}
          {series.map((s, si) => {
            const pts = base.map((p, i) => { const v = byDay[si].get(p.day); return v == null ? null : `${x(i)},${y(v)}`; }).filter(Boolean).join(' ');
            return <polyline key={s.name} points={pts} fill="none" stroke={s.color} strokeWidth={si === 0 ? 2 : 1.5}
                             strokeLinejoin="round" strokeLinecap="round" strokeDasharray={si === 0 ? undefined : '4 3'} />;
          })}
          {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + innerH} stroke="#334155" />}
          {hover !== null && series.map((s, si) => {
            const v = byDay[si].get(base[hover].day);
            return v == null ? null : <circle key={s.name} cx={x(hover)} cy={y(v)} r={4} fill={s.color} stroke={t.panel} strokeWidth={2} />;
          })}
          <rect x={PAD.left} y={PAD.top} width={innerW} height={innerH} fill="transparent"
                onMouseMove={(e) => {
                  const rect = (e.currentTarget as SVGRectElement).getBoundingClientRect();
                  const i = Math.round(((e.clientX - rect.left) / rect.width) * (base.length - 1));
                  setHover(Math.max(0, Math.min(base.length - 1, i)));
                }} />
        </svg>
        {hover !== null && (
          <Tip x={x(hover)} w={w}>
            <Typography variant="caption" sx={{ color: t.text2 }}>
              {new Date(base[hover].day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </Typography>
            {series.map((s, si) => {
              const v = byDay[si].get(base[hover].day);
              const first = s.points[0]?.value;
              return v == null ? null : (
                <Row key={s.name} k={s.name} v={<>{inr(v)} <span style={{ color: t.text2, fontWeight: 400 }}>({first ? ((v / first - 1) * 100).toFixed(1) : '0.0'}%)</span></>} />
              );
            })}
          </Tip>
        )}
      </Box>
    </Box>
  );
};

export const MonthlyBars: React.FC<{ data: MonthPoint[] }> = ({ data }) => {
  const { ref, w } = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const values = data.map((d) => d.pnl);
  const ticks = useMemo(() => niceTicks(Math.min(...values), Math.max(...values)), [values]);
  if (!data.length) return null;
  const yMin = ticks[0], yMax = ticks[ticks.length - 1];
  const innerW = w - PAD.left - PAD.right, innerH = H - PAD.top - PAD.bottom;
  const band = innerW / data.length;
  const x = (i: number) => PAD.left + band * i + band / 2;
  const y = (v: number) => PAD.top + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;
  const labelEvery = Math.ceil(data.length / Math.max(1, Math.floor(innerW / 64)));

  return (
    <Box ref={ref} sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
      <svg width={w} height={H} role="img" aria-label="Profit and loss per month" style={{ display: 'block' }}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={w - PAD.right} y1={y(v)} y2={y(v)} stroke={v === 0 ? '#334155' : t.border} strokeWidth={1} />
            <text x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end" fill={t.text3} fontSize={11}>{compact(v)}</text>
          </g>
        ))}
        {data.map((d, i) => i % labelEvery === 0 && (
          <text key={d.month} x={x(i)} y={H - 8} textAnchor="middle" fill={t.text3} fontSize={11}>{fmtMonth(d.month)}</text>
        ))}
        {data.map((d, i) => {
          const bw = Math.min(24, Math.max(3, band - 2));
          const top = y(Math.max(d.pnl, 0)), bottom = y(Math.min(d.pnl, 0));
          return <rect key={d.month} x={x(i) - bw / 2} y={top} width={bw} height={Math.max(1, bottom - top)} rx={2}
                       fill={d.pnl >= 0 ? t.profitMark : t.lossMark} />;
        })}
        {data.map((d, i) => (
          <rect key={d.month} x={PAD.left + band * i} y={PAD.top} width={band} height={innerH} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null && (
        <Tip x={x(hover)} w={w}>
          <Typography variant="caption" sx={{ color: t.text2 }}>{fmtMonth(data[hover].month)}</Typography>
          <Row k="P&L" v={inr(data[hover].pnl, true)} />
        </Tip>
      )}
    </Box>
  );
};
